import { memo } from 'react';

import { Button } from '@picsart/design-system/components/Button';
import { IconGenAI } from '@picsart/design-system/foundation/icons/IconGenAI';
import { IconCoins } from '@picsart/design-system/foundation/icons/IconCoins';
import { IconBookmarkFilled } from '@picsart/design-system/foundation/icons/IconBookmarkFilled';
import { IconBookmarkOutline } from '@picsart/design-system/foundation/icons/IconBookmarkOutline';
import { IconEditOutline } from '@picsart/design-system/foundation/icons/IconEditOutline';
import { IconShareOutline } from '@picsart/design-system/foundation/icons/IconShareOutline';
import { useTranslator } from '@picsart/rc/services/localization';
import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import useSavedTemplates from '@/hooks/useSavedTemplates';
import useHideFeedCredits from '@/hooks/useHideFeedCredits';
import PresetLikeButton from '@/components/PresetLikeButton';
import { usePresetShare } from '@/features/preset-sharing';
import { isPortfolioApp as checkIsPortfolioApp } from '@/utils/environment/surface';
import { ScreenType } from '@/types/analytics';
import { useProjectCredits } from '@/features/credits';
import Spinner from '@/components/Spinner';
import useStyles from './styles';

import type { FeedActionBarProps } from './types';

const FeedActionBar = ({
  label,
  shareTarget,
  onUpload,
  isLoading,
  documentId,
  slug,
  flowId,
  shouldLoadCredits,
  canEdit,
  onEdit,
}: FeedActionBarProps) => {
  const classes = useStyles();
  const translate = useTranslator();
  const isPortfolioApp = checkIsPortfolioApp();
  const { isSaved, toggleSaved } = useSavedTemplates();
  const { sharePreset, sharingSlug } = usePresetShare({
    screenType: ScreenType.PRESET_FEED,
  });
  const hideCredits = useHideFeedCredits();
  // Quoted by the calculator API rather than read off the CMS preset.
  // `FeedInputsSheet` shares this query's cache entry and applies the same
  // hide gate. Skipped once the remote setting resolves hidden.
  const { credits, isLoading: areCreditsLoading } = useProjectCredits({
    flowId,
    enabled: shouldLoadCredits && !hideCredits,
  });
  const saved = isSaved(slug);
  const SaveIcon = saved ? IconBookmarkFilled : IconBookmarkOutline;
  const isSharing = sharingSlug === slug;

  const handleShare = () => {
    sharePreset(shareTarget).catch((error) =>
      errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN)
    );
  };

  return (
    <div className={classes.actionBar}>
      {/* One rail for every secondary action, so Edit / Save / Share stack
          without any of them needing an offset that encodes the others'
          heights. Save is the only portfolio-gated one; the rest ship on all
          touchpoints. No Copy link here — Share reaches the OS sheet, which
          carries its own copy entry. */}
      <div className={classes.sideRail}>
        {canEdit && (
          <button
            type="button"
            className={classes.railButton}
            onClick={onEdit}
            aria-label="Edit inputs"
            data-testid="feed-edit"
          >
            <IconEditOutline className={classes.railIcon} fill="currentColor" />
            <span className={classes.railLabel}>{translate('Edit')}</span>
          </button>
        )}

        <PresetLikeButton
          documentId={documentId}
          slug={slug}
          screenType={ScreenType.PRESET_FEED}
        />

        {isPortfolioApp && (
          <button
            type="button"
            className={classes.railButton}
            onClick={() => toggleSaved(slug, ScreenType.PRESET_FEED)}
            aria-label={saved ? 'Remove from saved' : 'Save'}
            data-testid="feed-save"
          >
            <SaveIcon className={classes.railIcon} fill="currentColor" />
            <span className={classes.railLabel}>{translate('Save')}</span>
          </button>
        )}

        {/* Ships on every touchpoint — the link's target app is resolved from
            the surface inside the hook, not branched on here. */}
        <button
          type="button"
          className={classes.railButton}
          onClick={handleShare}
          disabled={isSharing}
          aria-label="Share"
          data-testid="feed-share"
        >
          {isSharing ? (
            <Spinner size="small" className={classes.railSpinner} />
          ) : (
            <IconShareOutline
              className={classes.railIcon}
              fill="currentColor"
            />
          )}
          <span className={classes.railLabel}>{translate('Share')}</span>
        </button>
      </div>

      <Button
        dataTestId="preset-feed-upload-button"
        className={classes.uploadButton}
        variant="filled"
        size="large"
        isFullWidth
        startIcon={IconGenAI}
        onClick={onUpload}
        isLoading={isLoading}
        isDisabled={isLoading || areCreditsLoading}
      >
        {label}
      </Button>

      {/* Deliberately not gated on `hideCredits`: a quote that started before
          the setting resolved hidden keeps `areCreditsLoading` true until it
          settles (disabling a query doesn't cancel its in-flight fetch), and
          the button above is disabled on it — this spinner is the only
          visible pending state. */}
      {areCreditsLoading && (
        <div
          className={classes.creditsChip}
          role="status"
          aria-label="Loading credit cost"
        >
          <Spinner size="xsmall" className={classes.creditsSpinner} />
        </div>
      )}

      {/* `!hideCredits` here also covers a quote already in the React Query
          cache from before the remote setting resolved. */}
      {!hideCredits && !areCreditsLoading && !!credits && credits > 0 && (
        <div
          className={classes.creditsChip}
          role="img"
          aria-label={`${credits} credits`}
        >
          <IconCoins
            className={classes.creditsIcon}
            fill="currentColor"
            aria-hidden="true"
          />
          <span aria-hidden="true">{credits}</span>
        </div>
      )}
    </div>
  );
};

export default memo(FeedActionBar);
